import React, { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { useI18n } from "../i18n";
import { getAdminStats } from "../lib/api";
import { getStoredUser } from "../components/RegisterModal";
import AdminFunnel from "../components/AdminFunnel";
import {
  Users, DollarSign, Flame, HeartPulse, TrendingUp, Crown,
  Loader2, AlertCircle, RefreshCw, ShieldCheck,
} from "lucide-react";

export default function Admin() {
  const { lang } = useI18n();
  const pt = lang === "pt";
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    setError(null);
    getAdminStats()
      .then((d) => { setData(d); setLoading(false); })
      .catch((e) => {
        const code = e?.response?.status;
        if (code === 401) setError("auth");
        else if (code === 403) setError("forbidden");
        else setError("network");
        setLoading(false);
      });
  };

  useEffect(() => { load(); }, []);

  const user = getStoredUser();
  if (!user?.email) return <Navigate to="/login" replace />;

  if (loading) {
    return (
      <div className="min-h-screen grid place-items-center" data-testid="admin-loading">
        <div className="flex items-center gap-3 text-neutral-400 font-mono text-sm uppercase tracking-[0.18em]">
          <Loader2 size={18} className="animate-spin text-gold-400" />
          {pt ? "Carregando…" : "Loading…"}
        </div>
      </div>
    );
  }

  if (error === "forbidden") {
    return (
      <div className="min-h-screen grid place-items-center px-6" data-testid="admin-forbidden">
        <div className="max-w-md text-center">
          <ShieldCheck size={48} className="mx-auto text-red-400" />
          <h1 className="mt-4 font-display text-2xl text-white">{pt ? "Acesso restrito" : "Restricted area"}</h1>
          <p className="mt-2 text-sm text-neutral-400">
            {pt
              ? "Esta página é exclusiva para administradores GOLDORA."
              : "This page is for GOLDORA admins only."}
          </p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen grid place-items-center px-6">
        <div className="max-w-md text-center">
          <AlertCircle size={48} className="mx-auto text-red-400" />
          <h1 className="mt-4 font-display text-2xl text-white">{pt ? "Erro" : "Error"}</h1>
          <p className="mt-2 text-sm text-neutral-400">
            {pt ? "Não foi possível carregar. Faça login novamente." : "Failed to load. Please log in again."}
          </p>
        </div>
      </div>
    );
  }

  const { users, plan_distribution, revenue, coach, top_streaks, recent_users, generated_at } = data;
  const fmtMoney = (n) => new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(n || 0);

  return (
    <div className="min-h-screen pt-24 pb-16" data-testid="page-admin">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="text-[10px] font-mono uppercase tracking-[0.22em] text-gold-400 flex items-center gap-1.5">
              <ShieldCheck size={11} /> {pt ? "Admin · Privado" : "Admin · Private"}
            </div>
            <h1 className="mt-2 font-display text-4xl tracking-tight text-white">
              {pt ? "Painel GOLDORA" : "GOLDORA dashboard"}
            </h1>
            <p className="mt-1 text-sm text-neutral-500 font-mono">
              {pt ? "Atualizado em" : "Updated at"} {new Date(generated_at).toLocaleString()}
            </p>
          </div>
          <button
            onClick={load}
            data-testid="admin-refresh"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-white/5 border border-white/10 text-neutral-300 hover:border-gold-500/40 hover:text-gold-300 transition-colors text-sm"
          >
            <RefreshCw size={14} />
            {pt ? "Atualizar" : "Refresh"}
          </button>
        </div>

        {/* Top KPIs */}
        <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="admin-kpis">
          <Kpi
            icon={Users}
            label={pt ? "Total de usuários" : "Total users"}
            value={users.total}
            sub={`+${users.new_today} ${pt ? "hoje" : "today"} · +${users.new_week} ${pt ? "7d" : "7d"} · +${users.new_month} ${pt ? "30d" : "30d"}`}
            tone="gold"
          />
          <Kpi
            icon={DollarSign}
            label={pt ? "MRR atual" : "Current MRR"}
            value={fmtMoney(revenue.mrr_usd)}
            sub={`${revenue.active_subscriptions} ${pt ? "assinantes" : "subscriptions"} · ${revenue.stripe_mode}`}
            tone="emerald"
          />
          <Kpi
            icon={HeartPulse}
            label={pt ? "Coach scores" : "Coach scores"}
            value={coach.total_scores_taken}
            sub={`${pt ? "Média" : "Avg"} ${coach.avg_score ?? "—"} / 100`}
            tone="rose"
          />
          <Kpi
            icon={Flame}
            label={pt ? "Check-ins hoje" : "Check-ins today"}
            value={coach.checkins_today}
            sub={`${coach.total_checkins} ${pt ? "no total" : "total"}`}
            tone="orange"
          />
        </div>

        {/* Conversion funnel + daily series */}
        <AdminFunnel />

        {/* Plan distribution + top streaks */}
        <div className="mt-8 grid lg:grid-cols-12 gap-6">
          <div className="lg:col-span-5 surface p-6">
            <div className="text-[10px] font-mono uppercase tracking-[0.22em] text-gold-400 flex items-center gap-1.5">
              <Crown size={11} /> {pt ? "Distribuição de planos" : "Plan distribution"}
            </div>
            <h2 className="mt-2 font-display text-xl text-white">{users.total} {pt ? "usuários" : "users"}</h2>
            <div className="mt-5 space-y-3" data-testid="admin-plans">
              {Object.entries(plan_distribution).map(([plan, count]) => {
                const pct = users.total ? Math.round((count / users.total) * 100) : 0;
                const tone = plan === "elite" ? "bg-gold-500" : plan === "pro" ? "bg-gold-400" : "bg-white/30";
                return (
                  <div key={plan}>
                    <div className="flex justify-between text-sm">
                      <span className="text-white capitalize">{plan}</span>
                      <span className="font-mono text-neutral-400">{count} · {pct}%</span>
                    </div>
                    <div className="mt-1.5 h-1.5 rounded bg-white/5 overflow-hidden">
                      <div className={`h-full ${tone} transition-all duration-500`} style={{ width: `${pct}%` }} />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="lg:col-span-7 surface p-6">
            <div className="text-[10px] font-mono uppercase tracking-[0.22em] text-gold-400 flex items-center gap-1.5">
              <Flame size={11} /> {pt ? "Top sequências" : "Top streaks"}
            </div>
            <h2 className="mt-2 font-display text-xl text-white">{pt ? "Os mais consistentes" : "Most consistent"}</h2>
            <div className="mt-4" data-testid="admin-streaks">
              {top_streaks.length === 0 ? (
                <div className="text-sm text-neutral-500 font-mono">{pt ? "Sem sequências ainda" : "No streaks yet"}</div>
              ) : (
                <ul className="space-y-2">
                  {top_streaks.map((s, i) => (
                    <li key={s.email} className="flex items-center justify-between px-3 py-2 rounded-md bg-white/[0.02] border border-white/5">
                      <span className="flex items-center gap-3">
                        <span className={`w-6 h-6 rounded-md grid place-items-center text-xs font-mono ${i === 0 ? "bg-gold-500 text-black" : "bg-white/5 text-neutral-400"}`}>{i + 1}</span>
                        <span className="text-sm text-neutral-200 truncate max-w-[220px]">{s.email}</span>
                      </span>
                      <span className="flex items-center gap-1.5 text-gold-300 font-mono text-sm">
                        <Flame size={13} className="flame-pulse" />
                        {s.current_streak}d <span className="text-neutral-600">· {s.longest_streak} max</span>
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </div>

        {/* Recent users */}
        <div className="mt-8 surface p-6">
          <div className="text-[10px] font-mono uppercase tracking-[0.22em] text-gold-400 flex items-center gap-1.5">
            <TrendingUp size={11} /> {pt ? "Usuários recentes" : "Recent users"}
          </div>
          <h2 className="mt-2 font-display text-xl text-white">{recent_users.length} {pt ? "registros" : "entries"}</h2>
          <div className="mt-4 overflow-x-auto" data-testid="admin-recent">
            <table className="w-full text-sm">
              <thead className="text-[10px] font-mono uppercase tracking-[0.18em] text-neutral-500">
                <tr className="border-b border-white/5">
                  <th className="text-left py-2 pr-4">Email</th>
                  <th className="text-left py-2 pr-4">{pt ? "Nome" : "Name"}</th>
                  <th className="text-left py-2 pr-4">Persona</th>
                  <th className="text-left py-2 pr-4">{pt ? "Plano" : "Plan"}</th>
                  <th className="text-left py-2 pr-4">{pt ? "Cadastro" : "Created"}</th>
                  <th className="text-left py-2">{pt ? "Última visita" : "Last seen"}</th>
                </tr>
              </thead>
              <tbody>
                {recent_users.map((u) => (
                  <tr key={u.email} className="border-b border-white/5 last:border-0 text-neutral-300">
                    <td className="py-2.5 pr-4 truncate max-w-[260px]">{u.email}</td>
                    <td className="py-2.5 pr-4">{u.name || "—"}</td>
                    <td className="py-2.5 pr-4 text-neutral-500">{u.persona || "—"}</td>
                    <td className="py-2.5 pr-4 capitalize">{u.plan || "free"}</td>
                    <td className="py-2.5 pr-4 font-mono text-xs text-neutral-500">{u.created_at?.slice(0, 10) || "—"}</td>
                    <td className="py-2.5 font-mono text-xs text-neutral-500">{u.last_seen?.slice(0, 16).replace("T", " ") || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}

const TONE = {
  gold:    { bg: "bg-gold-500/10",    border: "border-gold-500/40",    fg: "text-gold-300" },
  emerald: { bg: "bg-emerald-500/10", border: "border-emerald-500/30", fg: "text-emerald-300" },
  rose:    { bg: "bg-rose-500/10",    border: "border-rose-500/30",    fg: "text-rose-300" },
  orange:  { bg: "bg-orange-500/10",  border: "border-orange-500/30",  fg: "text-orange-300" },
};

function Kpi({ icon: Icon, label, value, sub, tone }) {
  const t = TONE[tone] || TONE.gold;
  return (
    <div className={`surface p-5 ${t.border}`} data-testid={`kpi-${tone}`}>
      <div className={`w-9 h-9 rounded-md grid place-items-center ${t.bg} border ${t.border} ${t.fg}`}>
        <Icon size={16} />
      </div>
      <div className="mt-3 text-[10px] font-mono uppercase tracking-[0.22em] text-neutral-500">{label}</div>
      <div className="mt-1 font-display text-3xl text-white">{value}</div>
      <div className="mt-2 text-xs text-neutral-500">{sub}</div>
    </div>
  );
}
